import type { Issue } from '../types/issue';
import { extractSystem } from './duplicate-detect';

// Cluster-Bildung über 3 Ebenen:
//   1) Bucket:      Profile · Beschläge · Fertigung (MCO) · Andere
//   2) Kategorie:   aus Component (Door, Window, Facade, Sliding, ALU, STEEL, …)
//   3) Serie:       aus Summary-Präfix (AWS 75, ADS 65, Janisol Arte 2.0, …)
//
// Primärquelle: JIRA-Feld `Components` (~94% Coverage). Fallback: Labels
// (falls Components leer). Ein Ticket kann in mehreren Buckets gleichzeitig
// landen (Multi-Component).

export type ClusterBucket = 'profile' | 'fittings' | 'mco' | 'other';

export const BUCKET_LABEL: Record<ClusterBucket, string> = {
  profile:  'Profile',
  fittings: 'Beschläge',
  mco:      'Fertigung (MCO)',
  other:    'Andere',
};

export const BUCKET_COLOR: Record<ClusterBucket, string> = {
  profile:  '#1976d2',
  fittings: '#ef6c00',
  mco:      '#7b1fa2',
  other:    '#9e9e9e',
};

// Keyword-Klassifikation (case-insensitive). Reihenfolge = Priorität:
// zuerst Beschläge testen, dann Profile — sonst würde „Profil" in
// „Profilschnitt_Beschlag" fälschlich als Profil klassifiziert.
const FITTING_KEYWORDS = [
  'klinkung', 'kloben', 'riegel', 'schloss', 'schließblech', 'schliessblech',
  'beschlag', 'beschläg', 'beschlaeg',
  'griff', 'handgriff', 'hand ', 'band', 'gelenk',
  'verbinder', 'befestigung', 'halterung', 'stopper',
  'fügung', 'fuegung', 'ecke', 'stoß', 'stoss',
  'avantec', 'dhs',
];

const PROFILE_KEYWORDS = [
  'profil', 'section', 'verglasung', 'dichtung', 'fassade', 'façade',
  'profilschnitt', 'schnitt',
];

export function classifyLabel(label: string): ClusterBucket {
  const s = label.toLowerCase();
  for (const kw of FITTING_KEYWORDS) if (s.includes(kw)) return 'fittings';
  for (const kw of PROFILE_KEYWORDS) if (s.includes(kw)) return 'profile';
  return 'other';
}

// Klassifikation aus JIRA-Component. Muster-Beispiele:
//   „item / window fittings"          → fittings · Kategorie „Window fittings"
//   „item / sliding fittings"         → fittings · „Sliding fittings"
//   „item / door fitting manager"     → fittings · „Door fitting manager"
//   „mco fittings"                    → fittings · „MCO fittings"
//   „item / door"                     → profile  · „Door"
//   „item / facade"                   → profile  · „Facade"
//   „item / window"                   → profile  · „Window"
//   „mco ALU"                         → mco      · „ALU"
//   „mco windows ALU"                 → mco      · „Windows ALU"
//   „mco STEEL"                       → mco      · „STEEL"
//   „database", „translation", …      → other    · Component-Name
export function classifyComponent(comp: string): { bucket: ClusterBucket; category: string } {
  const raw = comp.trim();
  const s = raw.toLowerCase();
  // Priorität 1: Beschläge — überall wo „fitting" drin ist
  if (s.includes('fitting')) {
    return { bucket: 'fittings', category: componentCategory(raw) };
  }
  // Priorität 2: MCO (Fertigung/Multi-Carrier-Optimierung)
  if (s.startsWith('mco ') || s === 'mco' || s.startsWith('mco/')) {
    return { bucket: 'mco', category: componentCategory(raw) };
  }
  // Priorität 3: Profile — item/<xxx> ohne fittings
  if (s.startsWith('item /') || s.startsWith('item/')) {
    return { bucket: 'profile', category: componentCategory(raw) };
  }
  // Explizit Profil-Wörter
  if (/profil|section/i.test(s)) {
    return { bucket: 'profile', category: componentCategory(raw) };
  }
  return { bucket: 'other', category: componentCategory(raw) };
}

// Kategorie-Extraktion aus Component. Team-artige Präfixe wie „door",
// „window", „sliding", „facade", „steel" werden entfernt — die Team-
// Zugehörigkeit kommt über den globalen Team-Filter, nicht über die
// Kategorie. So werden „item / window fittings" und „item / door fittings"
// beide zu Kategorie „Fittings".
//
// Beispiele:
//   „item / window fittings"          → „Fittings"
//   „item / door fittings"            → „Fittings"
//   „item / door fitting manager"     → „Fitting manager"
//   „item / sliding fittings"         → „Fittings"
//   „item / folding sliding fittings" → „Folding sliding fittings"
//   „item / door"                     → „Element"
//   „item / window"                   → „Element"
//   „item / sun shading"              → „Sun shading"
//   „mco ALU"                         → „ALU"
//   „mco windows ALU"                 → „ALU"
//   „mco doors ALU"                   → „ALU"
//   „mco STEEL"                       → „STEEL"
// Nach dem Team-Wort muss entweder Whitespace ODER Ende folgen — damit auch
// „item / door" (nur reines Team) zu leer wird und dann als „Element" landet.
const TEAM_PREFIX_RE = /^(doors?|windows?|sliding|facade|steel|stahl)(\s+|$)/i;
function componentCategory(raw: string): string {
  let s = raw
    .replace(/^item\s*\/\s*/i, '')
    .replace(/^mco\s+/i, '')
    .trim();
  // Team-Marker am Anfang entfernen (nur wenn es ein eigenständiges Präfix ist,
  // nicht Teil eines Compound-Worts wie „folding sliding")
  s = s.replace(TEAM_PREFIX_RE, '').trim();
  if (!s) return 'Element';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Serien-Normalisierung: aggressives Zusammenfassen von Produkt-Varianten
// zur Basis-Serie. Nur echte Produkt-Serien mit `<Wort> <Zahl>`-Muster
// kommen zurück — generische Wörter (clone, autotest, france, „neues
// feature", Marketing-Namen) werden zu null, damit die Aufrufer sie zu
// einem gemeinsamen „(kein Produkt-Präfix)"-Bucket zusammenlegen können.
//
// Beispiele:
//   „aws 75 wf.si+"                     → „AWS 75"
//   „aws 75.si+"                        → „AWS 75"
//   „ads 65 hd"                         → „ADS 65"
//   „[lixil] fws 50 ob | determination" → „FWS 50"    (bracket-Präfix weg)
//   „firestop ads 90"                   → „FireStop ADS 90" (Familie erhalten)
//   „janisol arte 2.0"                  → „Janisol Arte 2.0"
//   „janisol 3 ei30"                    → „Janisol 3"
//   „clone"                             → null
//   „autotest"                          → null
//   „neues feature"                     → null
//   „schüco perfect"                    → null
const SERIES_RE = /([A-Za-zäöüÄÖÜ]+(?:\s+[A-Za-zäöüÄÖÜ]+)?\s+\d+(?:\.\d+)?)/;
const BRACKET_PREFIX_RE = /^\s*\[[^\]]+\]\s*/;
export function normalizeSeries(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Bracket-Präfix vorne entfernen: „[LIXIL] fws 50 …" → „fws 50 …"
  let s = raw.trim().replace(BRACKET_PREFIX_RE, '');
  if (!s) return null;
  const m = s.match(SERIES_RE);
  if (!m) return null;   // kein Produkt-Muster → zusammenwerfen als „(kein Produkt-Präfix)"
  const base = m[1];
  return titleCaseProduct(base);
}

// Produkt-Codes wie AWS/ADS/DHS/FWS bleiben durchgängig Uppercase;
// „firestop"-artige Präfixe kriegen Title-Case; Zahlen bleiben.
function titleCaseProduct(s: string): string {
  return s
    .split(/\s+/)
    .map(w => {
      if (/^[a-z]{2,5}$/.test(w) && /^(aws|ads|adu|ase|ass|fws|dhs|viss|udc|mco|af|as|aw|ct|pd|icd|ldc|fd)$/i.test(w)) {
        return w.toUpperCase();
      }
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

// Konstante für Tickets ohne erkennbares Produkt-Muster
export const SERIES_UNKNOWN = '(kein Produkt-Präfix)';
export const FAMILY_UNKNOWN = '(keine Familie)';

// Extrahiert die Produkt-Familie aus einer normalisierten Serie.
// Beispiele:
//   „AWS 75"                 → „AWS"
//   „AWS 90"                 → „AWS"
//   „ADS 65"                 → „ADS"
//   „FireStop ADS 90"        → „FireStop ADS"    (Sonderfall: Zwei-Wort-Familie)
//   „Janisol 3"              → „Janisol"
//   „Janisol Arte 2.0"       → „Janisol Arte"
//   „AD UP 75"               → „AD UP"
//   „AS PD 75"               → „AS PD"
//   „(kein Produkt-Präfix)"  → „(keine Familie)"
const FAMILY_RE = /^([A-Za-zäöüÄÖÜ]+(?:\s+[A-Za-zäöüÄÖÜ]+)*)\s+\d/;
export function seriesToFamily(series: string | null | undefined): string {
  if (!series || series === SERIES_UNKNOWN) return FAMILY_UNKNOWN;
  const m = series.match(FAMILY_RE);
  if (!m) return series;   // Serie ohne Zahl (z.B. „VISS TVS") → Familie = Serie
  return m[1].trim();
}

// Kategorie-Extraktion aus Label. Beispiele:
//   „AW/AD_Klinkung"                              → „Klinkung"
//   „AW/AD_Belüftung_Entwässerung"                → „Belüftung/Entwässerung"
//   „AW/AD_Eck-_und_T-Verbinder/Stoßverbinder"    → „Eck-/T-Verbinder"
//   „Profilschnitt"                               → „Profilschnitt"
//   „Verglasung"                                  → „Verglasung"
//   „MCO-SB"                                      → „MCO-SB"
export function labelToCategory(label: string): string {
  // Präfix „AW/AD_" (und ähnlich) abschneiden
  let s = label
    .replace(/^AW\/AD[_-]?/i, '')
    .replace(/^AD[_-]/i, '')
    .replace(/^AW[_-]/i, '')
    .replace(/^MCO[_-]/i, 'MCO ');
  // Underscores durch Spaces / Slashes umwandeln
  s = s.replace(/_und_/gi, '/');
  s = s.replace(/[_]+/g, ' ');
  return s.trim();
}

// Ein einzelnes Ticket in seine Cluster-Zuordnungen (mehrere möglich).
export type TicketClassification = {
  buckets:     Set<ClusterBucket>;
  categories:  Map<ClusterBucket, Set<string>>;  // bucket → { kategorie1, kategorie2, … }
  series:      string | null;                     // aus Summary-Präfix
};

export function classifyTicket(row: Issue): TicketClassification {
  const c: TicketClassification = {
    buckets:    new Set(),
    categories: new Map(),
    series:     normalizeSeries(extractSystem(String(row['Summary'] ?? ''))),
  };
  const addCat = (b: ClusterBucket, cat: string) => {
    c.buckets.add(b);
    let set = c.categories.get(b);
    if (!set) { set = new Set(); c.categories.set(b, set); }
    if (cat) set.add(cat);
  };

  // Primärquelle: JIRA-Feld `Components` (bei ~94% der Tickets gepflegt).
  const comp = (row as Record<string, unknown>)['Components'];
  if (comp != null && comp !== '') {
    const arr = Array.isArray(comp) ? comp : [comp];
    for (const raw of arr) {
      const s = String(raw ?? '').trim();
      if (!s) continue;
      const { bucket, category } = classifyComponent(s);
      addCat(bucket, category);
    }
  }

  // Fallback: wenn kein Components-Feld → Labels als Klassifikations-Quelle
  if (c.buckets.size === 0) {
    const labels = (row as Record<string, unknown>)['Labels'];
    if (labels != null && labels !== '') {
      const arr = Array.isArray(labels) ? labels : [labels];
      for (const raw of arr) {
        const label = String(raw ?? '').trim();
        if (!label) continue;
        const b = classifyLabel(label);
        const cat = labelToCategory(label);
        addCat(b, cat);
      }
    }
  }

  if (c.buckets.size === 0) c.buckets.add('other');
  return c;
}

// ─── Aggregation ─────────────────────────────────────────────────────────

export type ClusterAggregation = {
  bucketCounts:    Record<ClusterBucket, number>;                                                          // unique tickets
  categoryCounts:  Map<ClusterBucket, Map<string, number>>;                                                // bucket → cat → n
  seriesCounts:    Map<ClusterBucket, Map<string, Map<string, number>>>;                                    // bucket → cat → serie → n
  familyCounts:    Map<ClusterBucket, Map<string, Map<string, number>>>;                                    // bucket → cat → familie → n
  seriesByFamily:  Map<ClusterBucket, Map<string, Map<string, Map<string, number>>>>;                       // bucket → cat → familie → serie → n
  familyByBucket:  Map<ClusterBucket, Map<string, number>>;                                                // bucket → familie → n (dedupliziert)
  seriesByBucket:  Map<ClusterBucket, Map<string, number>>;                                                // bucket → serie   → n (dedupliziert)
};

export function aggregateClusters(rows: Issue[]): ClusterAggregation {
  const bucketKeys: Record<ClusterBucket, Set<string>> = {
    profile: new Set(), fittings: new Set(), mco: new Set(), other: new Set(),
  };
  const catKeys = new Map<ClusterBucket, Map<string, Set<string>>>();
  const serKeys = new Map<ClusterBucket, Map<string, Map<string, Set<string>>>>();
  const famKeys = new Map<ClusterBucket, Map<string, Map<string, Set<string>>>>();
  const sByFamKeys = new Map<ClusterBucket, Map<string, Map<string, Map<string, Set<string>>>>>();
  // Bucket-level dedup (Ticket zählt pro Bucket nur einmal, auch bei Multi-Kategorie)
  const bucketFamKeys = new Map<ClusterBucket, Map<string, Set<string>>>();
  const bucketSerKeys = new Map<ClusterBucket, Map<string, Set<string>>>();

  for (const r of rows) {
    const key = String(r['Issue key'] ?? '');
    if (!key) continue;
    const c = classifyTicket(r);
    const ser = c.series ?? SERIES_UNKNOWN;
    const fam = seriesToFamily(c.series);

    for (const b of c.buckets) {
      bucketKeys[b].add(key);
      const cats = c.categories.get(b);
      const catList = (cats && cats.size) ? Array.from(cats) : ['(unkategorisiert)'];

      // Bucket-Level dedup (unabhängig von Kategorie)
      let bFam = bucketFamKeys.get(b); if (!bFam) { bFam = new Map(); bucketFamKeys.set(b, bFam); }
      let bFks = bFam.get(fam);        if (!bFks) { bFks = new Set(); bFam.set(fam, bFks); }
      bFks.add(key);
      let bSer = bucketSerKeys.get(b); if (!bSer) { bSer = new Map(); bucketSerKeys.set(b, bSer); }
      let bSks = bSer.get(ser);        if (!bSks) { bSks = new Set(); bSer.set(ser, bSks); }
      bSks.add(key);

      let byCat    = catKeys.get(b);       if (!byCat)    { byCat    = new Map(); catKeys.set(b, byCat); }
      let byCatSer = serKeys.get(b);       if (!byCatSer) { byCatSer = new Map(); serKeys.set(b, byCatSer); }
      let byCatFam = famKeys.get(b);       if (!byCatFam) { byCatFam = new Map(); famKeys.set(b, byCatFam); }
      let byCatFamSer = sByFamKeys.get(b); if (!byCatFamSer) { byCatFamSer = new Map(); sByFamKeys.set(b, byCatFamSer); }

      for (const cat of catList) {
        let ks = byCat.get(cat);         if (!ks) { ks = new Set(); byCat.set(cat, ks); }
        ks.add(key);
        let bySer = byCatSer.get(cat);   if (!bySer) { bySer = new Map(); byCatSer.set(cat, bySer); }
        let sks = bySer.get(ser);        if (!sks) { sks = new Set(); bySer.set(ser, sks); }
        sks.add(key);
        let byFam = byCatFam.get(cat);   if (!byFam) { byFam = new Map(); byCatFam.set(cat, byFam); }
        let fks = byFam.get(fam);        if (!fks) { fks = new Set(); byFam.set(fam, fks); }
        fks.add(key);
        let byFamMap = byCatFamSer.get(cat); if (!byFamMap) { byFamMap = new Map(); byCatFamSer.set(cat, byFamMap); }
        let inFam = byFamMap.get(fam);   if (!inFam) { inFam = new Map(); byFamMap.set(fam, inFam); }
        let fsks = inFam.get(ser);       if (!fsks) { fsks = new Set(); inFam.set(ser, fsks); }
        fsks.add(key);
      }
    }
  }

  const bucketCounts: Record<ClusterBucket, number> = {
    profile:  bucketKeys.profile.size,
    fittings: bucketKeys.fittings.size,
    mco:      bucketKeys.mco.size,
    other:    bucketKeys.other.size,
  };
  const categoryCounts = new Map<ClusterBucket, Map<string, number>>();
  for (const [b, m] of catKeys.entries()) {
    const nm = new Map<string, number>();
    for (const [cat, ks] of m.entries()) nm.set(cat, ks.size);
    categoryCounts.set(b, nm);
  }
  const seriesCounts = mapMapMapToCount(serKeys);
  const familyCounts = mapMapMapToCount(famKeys);
  const seriesByFamily = new Map<ClusterBucket, Map<string, Map<string, Map<string, number>>>>();
  for (const [b, byCat] of sByFamKeys.entries()) {
    const m1 = new Map<string, Map<string, Map<string, number>>>();
    for (const [cat, byFam] of byCat.entries()) {
      const m2 = new Map<string, Map<string, number>>();
      for (const [fam, bySer] of byFam.entries()) {
        const m3 = new Map<string, number>();
        for (const [s, ks] of bySer.entries()) m3.set(s, ks.size);
        m2.set(fam, m3);
      }
      m1.set(cat, m2);
    }
    seriesByFamily.set(b, m1);
  }
  const familyByBucket = new Map<ClusterBucket, Map<string, number>>();
  for (const [b, m] of bucketFamKeys.entries()) {
    const nm = new Map<string, number>();
    for (const [fam, ks] of m.entries()) nm.set(fam, ks.size);
    familyByBucket.set(b, nm);
  }
  const seriesByBucket = new Map<ClusterBucket, Map<string, number>>();
  for (const [b, m] of bucketSerKeys.entries()) {
    const nm = new Map<string, number>();
    for (const [ser, ks] of m.entries()) nm.set(ser, ks.size);
    seriesByBucket.set(b, nm);
  }
  return { bucketCounts, categoryCounts, seriesCounts, familyCounts, seriesByFamily, familyByBucket, seriesByBucket };
}

// Utility: Map<B, Map<C, Map<K, Set<X>>>> → Map<B, Map<C, Map<K, number>>>
function mapMapMapToCount<B, C, K>(
  src: Map<B, Map<C, Map<K, Set<string>>>>,
): Map<B, Map<C, Map<K, number>>> {
  const out = new Map<B, Map<C, Map<K, number>>>();
  for (const [b, m1] of src.entries()) {
    const nn = new Map<C, Map<K, number>>();
    for (const [c, m2] of m1.entries()) {
      const nnn = new Map<K, number>();
      for (const [k, s] of m2.entries()) nnn.set(k, s.size);
      nn.set(c, nnn);
    }
    out.set(b, nn);
  }
  return out;
}
